import React, { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Alert,
  Snackbar,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  IconButton,
  Tooltip,
  Autocomplete,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings'
import { itsmGroupApi, itsmProductApi, crqDeploymentApi, certificateApi } from '../utils/api'

function validateEmail(value) {
  if (!value || !value.trim()) return ''
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  const emails = value.split(',').map((e) => e.trim())
  for (const email of emails) {
    if (!emailRegex.test(email)) return `Invalid email: ${email}`
  }
  return ''
}

const EMPTY_CRQ_FORM = {
  environment: '',
  certs: [],
  itsm_product: null,
  crq_type: 'RENEWAL',
  execution_date: '',
}

const EMPTY_FORM = { itsm_group_name: '', requested_for_mail: '', manager_group_name: '', distribution_dl: '', venafi_subfolder: '', itsm_products: [] }

export default function Admin() {
  const [form, setForm] = useState(EMPTY_FORM)
  const [errors, setErrors] = useState({})
  const [saving, setSaving] = useState(false)

  const [dlRows, setDlRows] = useState([])
  const [dlDeleting, setDlDeleting] = useState(null)

  const [itsmProducts, setItsmProducts] = useState([])
  const [loadingProducts, setLoadingProducts] = useState(false)

  const [allCerts, setAllCerts] = useState([])
  const [loadingCerts, setLoadingCerts] = useState(false)
  const [certs, setCerts] = useState([])

  const [crqForm, setCrqForm] = useState(EMPTY_CRQ_FORM)
  const [crqErrors, setCrqErrors] = useState({})
  const [generatingCrq, setGeneratingCrq] = useState(false)
  const [generatedCrq, setGeneratedCrq] = useState(null)
  const [forceRelinkDialog, setForceRelinkDialog] = useState({ open: false, blockedBy: null })

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' })

  useEffect(() => {
    fetchDistributionDls()
    fetchItsmProducts()
    fetchAllCerts()
  }, [])

  // Filter allCerts client-side whenever environment or ITSM product changes
  useEffect(() => {
    if (!crqForm.environment || !crqForm.itsm_product) {
      setCerts([])
      return
    }
    setCerts(
      allCerts.filter(
        c => c.product_code === crqForm.itsm_product.product_code && c.sdlc === crqForm.environment
      )
    )
  }, [crqForm.environment, crqForm.itsm_product?.product_code, allCerts])

  async function fetchAllCerts() {
    setLoadingCerts(true)
    try {
      const res = await certificateApi.getAll({ limit: 10000 })
      setAllCerts(res.data.items || [])
    } catch {
      // silently ignore
    } finally {
      setLoadingCerts(false)
    }
  }

  function handleCertInputChange() {}

  async function fetchItsmProducts() {
    setLoadingProducts(true)
    try {
      const res = await itsmProductApi.getOnboardedOnprem()
      setItsmProducts(res.data.items || [])
    } catch {
      // silently ignore
    } finally {
      setLoadingProducts(false)
    }
  }

  async function fetchDistributionDls() {
    try {
      const res = await itsmGroupApi.getAll()
      setDlRows(res.data.items || [])
    } catch {
      // silently ignore
    }
  }

  function showSnackbar(message, severity = 'success') {
    setSnackbar({ open: true, message, severity })
  }

  function validate() {
    const errs = {}
    if (!form.itsm_group_name.trim()) errs.itsm_group_name = 'Required'
    if (!form.requested_for_mail.trim()) {
      errs.requested_for_mail = 'Required'
    } else {
      const singleEmailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!singleEmailRegex.test(form.requested_for_mail.trim())) errs.requested_for_mail = 'Invalid email'
    }
    if (!form.distribution_dl.trim()) {
      errs.distribution_dl = 'Required'
    } else {
      const emailErr = validateEmail(form.distribution_dl)
      if (emailErr) errs.distribution_dl = emailErr
    }
    setErrors(errs)
    return Object.keys(errs).length === 0
  }

  async function handleSave() {
    if (!validate()) return
    setSaving(true)
    try {
      try {
        await itsmGroupApi.create({
          itsm_group: form.itsm_group_name.trim(),
          requested_for_mail: form.requested_for_mail.trim() || null,
          manager_group_name: form.manager_group_name.trim() || null,
          distribution_dl: form.distribution_dl.trim() || null,
          venafi_subfolder: form.venafi_subfolder.trim() || null,
          itsm_products: form.itsm_products.map((p) => p.product_code),
        })
      } catch (err) {
        // 409 means group already exists — update it instead
        if (err.response?.status === 409) {
          const listRes = await itsmGroupApi.getAll()
          const existing = (listRes.data.items || []).find(
            (g) => g.itsm_group.toLowerCase() === form.itsm_group_name.trim().toLowerCase()
          )
          if (!existing) throw new Error('ITSM group not found after conflict')
          await itsmGroupApi.update(existing.id, {
            requested_for_mail: form.requested_for_mail.trim() || null,
            manager_group_name: form.manager_group_name.trim() || null,
            distribution_dl: form.distribution_dl.trim() || null,
            venafi_subfolder: form.venafi_subfolder.trim() || null,
            itsm_products: form.itsm_products.map((p) => p.product_code),
          })
        } else {
          throw err
        }
      }

      showSnackbar('Record saved')
      setForm(EMPTY_FORM)
      setErrors({})
      fetchDistributionDls()
    } catch (err) {
      showSnackbar(err.response?.data?.detail || err.message || 'Save failed', 'error')
    } finally {
      setSaving(false)
    }
  }

  async function handleDlDelete(id) {
    setDlDeleting(id)
    try {
      await itsmGroupApi.delete(id)
      showSnackbar('Entry deleted')
      fetchDistributionDls()
    } catch (err) {
      showSnackbar(err.response?.data?.detail || 'Delete failed', 'error')
    } finally {
      setDlDeleting(null)
    }
  }

  function validateCrqForm() {
    const errs = {}
    if (!crqForm.environment) errs.environment = 'Required'
    if (!crqForm.certs.length) errs.certs = 'Select at least one certificate'
    if (!crqForm.itsm_product) errs.itsm_product = 'Required'
    if (!crqForm.execution_date) errs.execution_date = 'Required'
    setCrqErrors(errs)
    return Object.keys(errs).length === 0
  }

  async function handleGenerateCrq(forceRelink = false) {
    if (!validateCrqForm()) return
    setGeneratingCrq(true)
    setGeneratedCrq(null)
    try {
      const res = await crqDeploymentApi.generate({
        cert_ids: crqForm.certs.map(c => c.cert_id),
        itsm_product_code: crqForm.itsm_product?.product_code,
        crq_type: crqForm.crq_type,
        execution_date: crqForm.execution_date,
        force_relink: forceRelink,
      })
      setGeneratedCrq(res.data)
      showSnackbar(`CRQ ${res.data.crq_number} generated successfully`)
      setCrqForm(EMPTY_CRQ_FORM)
      setCrqErrors({})
    } catch (err) {
      if (err.response?.status === 409) {
        const detail = err.response?.data?.detail || ''
        const match = detail.match(/CRQ\d+/)
        setForceRelinkDialog({ open: true, blockedBy: match ? match[0] : 'an existing CRQ' })
      } else {
        showSnackbar(err.response?.data?.detail || 'CRQ generation failed', 'error')
      }
    } finally {
      setGeneratingCrq(false)
    }
  }

  async function handleConfirmForceRelink() {
    setForceRelinkDialog({ open: false, blockedBy: null })
    await handleGenerateCrq(true)
  }

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', p: { xs: 2, md: 3 }, display: 'flex', flexDirection: 'column', gap: 4 }}>

      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 2,
          background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <AdminPanelSettingsIcon sx={{ fontSize: 40 }} />
        <Box>
          <Typography variant="h5" fontWeight={700}>Admin</Typography>
          <Typography variant="body2" sx={{ opacity: 0.85 }}>
            Manage ITSM groups and distribution lists
          </Typography>
        </Box>
      </Paper>

      {/* Combined ITSM Group entry */}
      <Paper elevation={1} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ px: 3, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h6" fontWeight={600}>Authorization Group</Typography>
        </Box>

        <Box sx={{ px: 3, py: 2.5, display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap', bgcolor: 'grey.50' }}>
          <TextField
            label="ITSM Group Name"
            size="small"
            value={form.itsm_group_name}
            onChange={(e) => setForm({ ...form, itsm_group_name: e.target.value })}
            error={!!errors.itsm_group_name}
            helperText={errors.itsm_group_name}
            autoComplete="off"
            sx={{ flex: '1 1 260px', minWidth: 220 }}
          />
          <TextField
            label="Requested For Mail"
            size="small"
            placeholder="email@example.com"
            value={form.requested_for_mail}
            onChange={(e) => setForm({ ...form, requested_for_mail: e.target.value })}
            error={!!errors.requested_for_mail}
            helperText={errors.requested_for_mail || 'Used as CRQ \'Requested For\' attribute'}
            autoComplete="off"
            sx={{ flex: '1 1 260px', minWidth: 220 }}
          />
          <TextField
            label="Manager Group Name"
            size="small"
            value={form.manager_group_name}
            onChange={(e) => setForm({ ...form, manager_group_name: e.target.value })}
            autoComplete="off"
            sx={{ flex: '1 1 260px', minWidth: 220 }}
          />
          <TextField
            label="Distribution DL"
            size="small"
            placeholder="email@example.com"
            value={form.distribution_dl}
            onChange={(e) => setForm({ ...form, distribution_dl: e.target.value })}
            error={!!errors.distribution_dl}
            helperText={errors.distribution_dl || 'The DL which the particular ITSM group uses'}
            autoComplete="off"
            sx={{ flex: '2 1 260px', minWidth: 220 }}
          />
          <TextField
            label="Venafi Subfolder"
            size="small"
            value={form.venafi_subfolder}
            onChange={(e) => setForm({ ...form, venafi_subfolder: e.target.value })}
            helperText="Folder under Automation; product name subfolder is created inside"
            autoComplete="off"
            sx={{ flex: '1 1 260px', minWidth: 220 }}
          />
          <Autocomplete
            multiple
            size="small"
            options={itsmProducts}
            loading={loadingProducts}
            getOptionLabel={(opt) => `${opt.product_name} (${opt.product_code})`}
            isOptionEqualToValue={(opt, val) => opt.product_code === val.product_code}
            value={form.itsm_products}
            onChange={(_, newVal) => setForm({ ...form, itsm_products: newVal })}
            renderTags={(value, getTagProps) =>
              value.map((opt, index) => (
                <Chip key={opt.product_code} label={opt.product_name} size="small" {...getTagProps({ index })} />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="ITSM Products"
                helperText="Optional — associate onboarded products with this group"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingProducts && <CircularProgress size={14} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            sx={{ flex: '2 1 320px', minWidth: 260 }}
          />
          <Box sx={{ display: 'flex', gap: 1, pt: 0.25 }}>
            <Button
              variant="contained"
              size="small"
              startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <AddIcon />}
              onClick={handleSave}
              disabled={saving}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Records table */}
      <Paper elevation={1} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ px: 3, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h6" fontWeight={600}>Saved Records</Typography>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell sx={{ fontWeight: 600 }}>ITSM Group</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Requested For Mail</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Manager Group Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Distribution DL</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Venafi Subfolder</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>ITSM Products</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 60 }} align="center">Delete</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {dlRows.length === 0 ? (
                <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ color: 'text.secondary', py: 3 }}>
                    No records saved yet
                  </TableCell>
                </TableRow>
              ) : (
                dlRows.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{row.itsm_group}</TableCell>
                    <TableCell>{row.requested_for_mail || '—'}</TableCell>
                    <TableCell>{row.manager_group_name || '—'}</TableCell>
                    <TableCell>{row.distribution_dl || '—'}</TableCell>
                    <TableCell>{row.venafi_subfolder || '—'}</TableCell>
                    <TableCell>
                      {(row.itsm_products && row.itsm_products.length > 0)
                        ? row.itsm_products.map((code) => {
                            const p = itsmProducts.find((x) => x.product_code === code)
                            return (
                              <Chip key={code} label={p ? p.product_name : code} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                            )
                          })
                        : '—'}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Delete">
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            disabled={dlDeleting === row.id}
                            onClick={() => handleDlDelete(row.id)}
                          >
                            {dlDeleting === row.id
                              ? <CircularProgress size={16} color="error" />
                              : <DeleteIcon fontSize="small" />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Generate CRQ */}
      <Paper elevation={1} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ px: 3, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h6" fontWeight={600}>Generate CRQ</Typography>
          <Typography variant="body2" color="text.secondary">
            Manually create a Renewal or Layer7 CRQ. Skipped if an open CRQ already exists for the product/environment.
          </Typography>
        </Box>

        <Box sx={{ px: 3, py: 2.5, display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap', bgcolor: 'grey.50' }}>
          {/* Environment */}
          <FormControl size="small" sx={{ flex: '1 1 140px', minWidth: 130 }} error={!!crqErrors.environment}>
            <InputLabel>Environment *</InputLabel>
            <Select
              label="Environment *"
              value={crqForm.environment}
              onChange={(e) => {
                const env = e.target.value
                setCrqForm(prev => ({ ...prev, environment: env, certs: [] }))
              }}
            >
              {['dev', 'qa', 'uat', 'prd'].map(env => (
                <MenuItem key={env} value={env}>{env.toUpperCase()}</MenuItem>
              ))}
            </Select>
            {crqErrors.environment && <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>{crqErrors.environment}</Typography>}
          </FormControl>

          {/* ITSM Product — select before certificates to scope the cert list */}
          <Autocomplete
            size="small"
            options={itsmProducts}
            loading={loadingProducts}
            getOptionLabel={(opt) => `${opt.product_name} (${opt.product_code})`}
            isOptionEqualToValue={(opt, val) => opt.product_code === val.product_code}
            value={crqForm.itsm_product}
            onChange={(_, v) => {
              setCrqForm(prev => ({ ...prev, itsm_product: v, certs: [] }))
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="ITSM Product *"
                error={!!crqErrors.itsm_product}
                helperText={crqErrors.itsm_product}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (<>{loadingProducts && <CircularProgress size={14} />}{params.InputProps.endAdornment}</>),
                }}
              />
            )}
            sx={{ flex: '2 1 260px', minWidth: 220 }}
          />

          {/* Certificates — filtered by selected environment + ITSM product */}
          <Autocomplete
            multiple
            size="small"
            disabled={!crqForm.environment || !crqForm.itsm_product}
            options={certs}
            loading={loadingCerts}
            getOptionLabel={(opt) => `${opt.common_name} (${opt.sdlc?.toUpperCase() ?? ''})`}
            isOptionEqualToValue={(opt, val) => opt.cert_id === val.cert_id}
            filterOptions={(options, state) =>
              options.filter(o =>
                o.common_name.toLowerCase().includes(state.inputValue.toLowerCase())
              )
            }
            value={crqForm.certs}
            onChange={(_, v) => { setCrqForm({ ...crqForm, certs: v }) }}
            renderTags={(value, getTagProps) =>
              value.map((opt, index) => (
                <Chip
                  key={opt.cert_id}
                  label={`${opt.common_name} (${opt.sdlc?.toUpperCase() ?? ''})`}
                  size="small"
                  {...getTagProps({ index })}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Certificates *"
                error={!!crqErrors.certs}
                helperText={crqErrors.certs || (!crqForm.environment || !crqForm.itsm_product ? 'Select environment and product first' : loadingCerts ? 'Loading…' : certs.length === 0 ? 'No certificates found for this product / environment' : `${certs.length} certificate${certs.length !== 1 ? 's' : ''} found — click to select`)}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (<>{loadingCerts && <CircularProgress size={14} />}{params.InputProps.endAdornment}</>),
                }}
              />
            )}
            sx={{ flex: '3 1 320px', minWidth: 320 }}
          />

          {/* CRQ Type */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant="caption" color="text.secondary">CRQ Type</Typography>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={crqForm.crq_type}
              onChange={(_, v) => v && setCrqForm({ ...crqForm, crq_type: v })}
            >
              <ToggleButton value="RENEWAL">Renewal</ToggleButton>
              <ToggleButton value="LAYER7">Layer7</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Date of Execution — sets both scheduled start (03:00) and end (06:30) on the backend */}
          <TextField
            label="Date of Execution *"
            type="date"
            size="small"
            value={crqForm.execution_date}
            onChange={(e) => setCrqForm({ ...crqForm, execution_date: e.target.value })}
            error={!!crqErrors.execution_date}
            helperText={crqErrors.execution_date || 'Start 03:00 UTC · End 06:30 UTC'}
            InputLabelProps={{ shrink: true }}
            sx={{ flex: '1 1 200px', minWidth: 180 }}
          />

          <Box sx={{ display: 'flex', gap: 1, pt: 0.25 }}>
            <Button
              variant="contained"
              size="small"
              startIcon={generatingCrq ? <CircularProgress size={14} color="inherit" /> : null}
              onClick={() => handleGenerateCrq()}
              disabled={generatingCrq}
            >
              Generate
            </Button>
          </Box>
        </Box>

        {/* Last generated CRQ result */}
        {generatedCrq && (
          <Box sx={{ px: 3, pb: 2 }}>
            <Alert severity="success" sx={{ mt: 1 }}>
              CRQ <strong>{generatedCrq.crq_number}</strong> created — {generatedCrq.crq_type} for{' '}
              {generatedCrq.product_name} in {generatedCrq.environment.toUpperCase()} environment &nbsp;|&nbsp;
              Scheduled: {generatedCrq.scheduled_start_date?.slice(0, 10)}
              {generatedCrq.certificates?.length > 0 && (
                <> &nbsp;| {generatedCrq.certificates.length} cert(s) linked</>
              )}
            </Alert>
          </Box>
        )}
      </Paper>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Force-relink confirm dialog */}
      <Dialog open={forceRelinkDialog.open} onClose={() => setForceRelinkDialog({ open: false, blockedBy: null })} maxWidth="xs" fullWidth>
        <DialogTitle>Certificate Already Linked</DialogTitle>
        <DialogContent>
          <DialogContentText>
            One or more selected certificates are already linked to <strong>{forceRelinkDialog.blockedBy}</strong>.
            Do you want to unlink them from that CRQ and generate a new one?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setForceRelinkDialog({ open: false, blockedBy: null })}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={handleConfirmForceRelink}>
            Unlink &amp; Generate New CRQ
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

